import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { VoiceInput } from '../components/VoiceInput';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Send, Bot } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const countryCodes = [
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+32', country: 'Belgium', flag: '🇧🇪' },
  { code: '+41', country: 'Switzerland', flag: '🇨🇭' },
  { code: '+352', country: 'Luxembourg', flag: '🇱🇺' },
  { code: '+377', country: 'Monaco', flag: '🇲🇨' },
  { code: '+212', country: 'Morocco', flag: '🇲🇦' },
  { code: '+216', country: 'Tunisia', flag: '🇹🇳' },
  { code: '+213', country: 'Algeria', flag: '🇩🇿' },
  { code: '+221', country: 'Senegal', flag: '🇸🇳' },
  { code: '+225', country: 'Ivory Coast', flag: '🇨🇮' },
  { code: '+237', country: 'Cameroon', flag: '🇨🇲' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷' },
  { code: '+52', country: 'Mexico', flag: '🇲🇽' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
];

const branches = [
  { value: 'formation', label: 'Formation' },
  { value: 'pme', label: 'PME' },
  { value: 'freelance', label: 'Freelance' },
  { value: 'employes', label: 'Employés de bureau' },
];

export const Register = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country_code: '+33',
    branch: searchParams.get('branch') || 'formation',
    needs: '',
    voice_transcript: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleVoiceTranscript = (transcript) => {
    setFormData(prev => ({
      ...prev,
      needs: prev.needs + (prev.needs ? ' ' : '') + transcript,
      voice_transcript: prev.voice_transcript + (prev.voice_transcript ? ' ' : '') + transcript
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.needs) {
      toast.error('Veuillez remplir tous les champs requis');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/clients`, formData);
      toast.success(t('success'));
      navigate(`/thank-you?clientId=${response.data.id}`);
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('error'));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50" data-testid="register-page">
      <div className="max-w-2xl mx-auto px-6">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Bot className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading font-bold text-3xl text-slate-900 mb-2">
            Décrivez vos besoins
          </h1>
          <p className="text-slate-600">
            Notre équipe vous contactera pour vous proposer la solution adaptée
          </p>
        </div>

        <Card className="shadow-lg border-0" data-testid="register-form-card">
          <CardHeader>
            <CardTitle className="text-lg">Vos informations</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name">{t('name')} *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Jean Dupont"
                  required
                  data-testid="register-name-input"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">{t('email')} *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="jean@exemple.com"
                  required
                  data-testid="register-email-input"
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone">{t('phone')}</Label>
                <div className="flex gap-2">
                  <Select
                    value={formData.country_code}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, country_code: value }))}
                  >
                    <SelectTrigger className="w-32" data-testid="register-country-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {countryCodes.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.flag} {country.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="06 12 34 56 78"
                    className="flex-1"
                    data-testid="register-phone-input"
                  />
                </div>
              </div>

              {/* Branch */}
              <div className="space-y-2">
                <Label>Secteur d'activité *</Label>
                <Select
                  value={formData.branch}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, branch: value }))}
                >
                  <SelectTrigger data-testid="register-branch-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((branch) => (
                      <SelectItem key={branch.value} value={branch.value}>
                        {branch.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Needs */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="needs">{t('needs')} *</Label>
                  <VoiceInput onTranscript={handleVoiceTranscript} />
                </div>
                <Textarea
                  id="needs"
                  name="needs"
                  value={formData.needs}
                  onChange={handleChange}
                  placeholder="Décrivez vos besoins, vos attentes et vos objectifs..."
                  rows={6}
                  required
                  data-testid="register-needs-textarea"
                />
                {formData.voice_transcript && (
                  <p className="text-xs text-slate-500">
                    {t('voiceTranscript')}: {formData.voice_transcript}
                  </p>
                )}
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-6"
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    {t('submit')}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
